import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";

import { Alert, Button, Col, InferenceIcon, Input } from "@kuzco/web/ui";

import { authClient } from "../auth-client";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  head: () => ({
    meta: [{ title: "Nubank Developer Console" }],
  }),
});

function LoginPage() {
  const { data: session } = authClient.useSession();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const hasRedirected = useRef(false);

  useEffect(() => {
    if (session?.user && !hasRedirected.current) {
      hasRedirected.current = true;
      void navigate({ to: "/" });
    }
  }, [session, navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    posthog.capture("login_magic_link_requested", { email });

    try {
      const result = await authClient.signIn.magicLink({
        callbackURL: window.location.origin,
        email,
        errorCallbackURL: `${window.location.origin}/login`,
        name: email.split("@")[0] || "NBDC user",
      });

      if (result.error) {
        const errorMessage =
          result.error.message ?? "Unable to send magic link";
        posthog.capture("login_magic_link_error", {
          email,
          error_message: errorMessage,
        });
        setError(errorMessage);
        return;
      }

      setSuccessMessage(
        "Check your inbox — if this email is allowed, a magic link is on its way.",
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred";
      posthog.capture("login_magic_link_error", {
        email,
        error_message: errorMessage,
      });
      posthog.captureException(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    if (error) setError(null);
  };

  return (
    <div className="flex min-h-screen w-full flex-col md:flex-row bg-background">
      {/* Left side: Form */}
      <div
        className={`
          flex w-full flex-col justify-between p-6
          md:w-1/2 md:p-10
          lg:w-[45%]
          xl:w-[40%]
        `}
      >
        {/* Top: Co-branded logo */}
        <div className="flex items-center gap-3">
          <InferenceIcon height={24} width={140} />
          <span aria-hidden className="text-base text-muted-foreground">
            ×
          </span>
          <img
            src="/nu_logo.svg"
            alt="Nu"
            style={{ height: 22, width: "auto" }}
          />
        </div>

        {/* Center: Form */}
        <div
          className={`
            flex w-full flex-1 items-center justify-center py-12
            md:py-0
          `}
        >
          <div className="w-full max-w-[400px]">
            <Col className="space-y-2">
              <h1
                className={`
                  font-serif text-3xl font-medium tracking-tight text-foreground
                `}
              >
                Welcome back
              </h1>
              <p className="text-sm font-light text-muted-foreground">
                Sign in with your Nubank email.
              </p>
            </Col>

            <form onSubmit={(e) => void handleSubmit(e)} className="mt-6">
              <Col className="space-y-3">
                <Input
                  autoComplete="email"
                  autoFocus
                  id="email"
                  inputMode="email"
                  label="Email"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError();
                  }}
                  placeholder="name@nubank.com.br"
                  type="email"
                  value={email}
                />
                <Button
                  className={`
                    w-full h-10 bg-[#00e275] text-[#001408] border-none
                    font-medium
                    hover:bg-[#00e275]/90
                  `}
                  disabled={loading || !email}
                  type="submit"
                >
                  {loading ? "Sending magic link..." : "Send magic link"}
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  We'll email you a one-time link to sign in. No password
                  required.
                </p>
              </Col>
            </form>

            {successMessage && (
              <Alert
                className={`
                  mt-4 border-detail-success/30 bg-detail-success/10
                  text-detail-success
                `}
              >
                <p>{successMessage}</p>
              </Alert>
            )}

            {error && (
              <Alert className="mt-4" variant="destructive">
                <p>{error}</p>
              </Alert>
            )}
          </div>
        </div>

        {/* Bottom: Footer */}
        <div
          className={`
            mx-auto mt-auto w-full max-w-[400px] text-center text-xs
            text-muted-foreground
          `}
        >
          By continuing, you agree to Inference's{" "}
          <a
            className="text-foreground underline-offset-4 hover:underline"
            href="https://inference.net/terms-of-service"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            className="text-foreground underline-offset-4 hover:underline"
            href="https://inference.net/privacy-policy"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </a>
          .
        </div>
      </div>

      {/* Right side: Splash image (hidden on mobile) */}
      <div
        className={`
          relative hidden w-full overflow-hidden border-l border-border/50
          md:block md:w-1/2
          lg:w-[55%]
          xl:w-[60%]
        `}
      >
        <img
          src="/nu-login-asset.jpeg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    </div>
  );
}
